import { useEffect, useMemo, useState } from "react";

import moment from "moment-timezone";

import { classes, Text } from "@alpacahq/ui/old";

import { getPercentageChange } from "../../../../../utils";
import {
  getPrettyAmount,
  getPrettyPercent,
} from "../../../../../utils/formatting";

type SnapshotState = {
  equity: string;
  dailyChange: string;
  date?: string;
};

export interface SnapshotProps {
  accountID?: string;
  equity?: string;
  lastEquity?: string;
  denormalize: (num: number) => number;
}

export const Snapshot = ({
  accountID,
  equity = "0",
  lastEquity = "0",
  denormalize,
}: SnapshotProps) => {
  const [hovering, setHovering] = useState(false);
  const [activePoint, setActivePoint] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // default snapshot state is the current equity value and daily change
  const defaultSnapshotState = useMemo<SnapshotState>(
    () => ({
      equity: getPrettyAmount(equity, 2, 2),
      dailyChange: getPrettyPercent(getPercentageChange(lastEquity, equity)),
      date: moment().toISOString(),
    }),
    [equity, lastEquity],
  );

  const [snapshotState, setSnapshotState] =
    useState<SnapshotState>(defaultSnapshotState);

  // biome-ignore lint/correctness/useExhaustiveDependencies: accountID is the trigger: the chart's hover and snapshot state is cleared when the user switches account. defaultSnapshotState is rebuilt every render, so listing it would clear the chart state on every render.
  useEffect(() => {
    setHovering(false);
    setActivePoint(null);
    setSnapshotState(defaultSnapshotState);
  }, [accountID]);

  // split the equity value into integer and decimal parts
  const [integer, decimal] = useMemo(
    () => snapshotState.equity.split("."),
    [snapshotState.equity],
  );

  // listen for active points on the chart
  useEffect(() => {
    const handle = (event: CustomEvent<{ x: number; y: number }>) => {
      setActivePoint({
        x: event.detail.x,
        y: denormalize(event.detail.y),
      });
    };
    window.addEventListener("activePoint", handle as EventListener);
    return () =>
      window.removeEventListener("activePoint", handle as EventListener);
  }, [denormalize]);

  useEffect(() => {
    const handle = () => {
      setHovering(false);
      setActivePoint(null);
      setSnapshotState(defaultSnapshotState);
    };

    window.addEventListener("activePointUnHover", handle);
    return () => window.removeEventListener("activePointUnHover", handle);
  }, [defaultSnapshotState]);

  // listen for mouse movement and update displayed information accordingly
  useEffect(() => {
    const handle = ({ clientX, clientY }: MouseEvent) => {
      const chart: HTMLElement | null = document.getElementById("chart");

      // if the chart is not rendered, do nothing
      if (!chart) {
        return;
      }

      const rect = chart.getBoundingClientRect();

      const x = clientX - rect.left;
      const y = clientY - rect.top;

      // if the mouse is outside the chart area, display current date and equity values
      if (!(x > 0 && x < rect.width && y > 0 && y < rect.height)) {
        setHovering(false);
        setActivePoint(null);
        setSnapshotState(defaultSnapshotState);
      } else if (activePoint) {
        setHovering(true);
        setSnapshotState({
          equity: getPrettyAmount(activePoint.y, 2, 2),
          dailyChange: getPrettyPercent(
            getPercentageChange(lastEquity, activePoint.y),
          ),
          date: moment(activePoint.x).toISOString(),
        });
      }
    };

    if (!hovering) {
      setSnapshotState(defaultSnapshotState);
    }

    // listen for mouse movement and clean up when done
    window.addEventListener("mousemove", handle);
    return () => window.removeEventListener("mousemove", handle);
  }, [activePoint, defaultSnapshotState, lastEquity, hovering]);

  return (
    <div>
      <div className="flex items-start">
        <Text className="flex items-start font-medium text-xl">
          <span className="mr-1.5 text-base text-gray-400">$</span>
          {integer}.{decimal}
        </Text>
        <Text
          className={classes(
            "ml-1.5 text-xl",
            snapshotState.dailyChange.includes("-")
              ? "text-red-500"
              : "text-green-500",
          )}
        >
          {snapshotState.dailyChange}
        </Text>
      </div>
      <Text className="mt-2 font-medium text-gray-300 text-sm">
        {moment(snapshotState.date)
          .tz(moment.tz.guess())
          .format("MMMM DD, hh:mm A z")}
      </Text>
    </div>
  );
};

export default Snapshot;
